import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { MGMT_GATEWAY } from '@workheld/workheld-shared-lib';
import { Observable, shareReplay } from 'rxjs';

const USER_PICTURE_PATTERN = new RegExp(`${MGMT_GATEWAY}/api/v1/users/[^/]+/picture`);
const CACHE_TTL_MS = 5 * 60 * 1000;
const MAX_CACHE_ENTRIES = 100;

interface CachedPicture {
  expiresAt: number;
  response$: Observable<HttpEvent<unknown>>;
}

@Injectable()
export class PictureCacheInterceptor implements HttpInterceptor {
  private readonly cache = new Map<string, CachedPicture>();

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    const cacheKey = req.urlWithParams;
    if (req.method !== 'GET' || !USER_PICTURE_PATTERN.test(cacheKey)) {
      return next.handle(req);
    }

    const cachedPicture = this.cache.get(cacheKey);
    if (cachedPicture && cachedPicture.expiresAt > Date.now()) {
      return cachedPicture.response$;
    }

    this.cache.delete(cacheKey);
    if (this.cache.size === MAX_CACHE_ENTRIES) {
      this.cache.delete(this.cache.keys().next().value);
    }

    const response$ = next.handle(req).pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    this.cache.set(cacheKey, { expiresAt: Date.now() + CACHE_TTL_MS, response$ });
    return response$;
  }
}

export const PICTURE_CACHE_INTERCEPTOR = {
  provide: HTTP_INTERCEPTORS,
  useClass: PictureCacheInterceptor,
  multi: true,
};
